import React, { useState, useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Profile } from "../lib/interface";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Redux/Store";
import { allPost, allPut } from "../Redux/Actions.tsx";
import { urlutenti } from "../lib/api.ts";
import { useAlert } from "./Alert.tsx";
import { Password } from "primereact/password";
import { addLocale } from "primereact/api";

// Aggiungi la localizzazione in italiano
addLocale("it", {
  weak: "Debole",
  medium: "Media",
  strong: "Forte",
  passwordPrompt: "Inserisci una password",
});

interface ModalFormUtentiProps {
  arrayUtenti: Profile[];
  onAddUtente: (nuovoUtente: Profile) => void;
  updateUtenti: (updatedUtente: Profile) => void;
  setArrayUtenti: (arrayUtenti: Profile[]) => void;
  action: "crea" | "modifica";
  isModalOpen: boolean;
  setModalOpen: (isOpen: boolean) => void;
  selectedUtente: Profile | null;
  setSelectedUtente: (utente: Profile | null) => void;
  onUtenteClick: (utente: Profile) => void;
}

const tipoUtenteOptions = [
  { label: "Account", value: "ACCOUNT" },
  { label: "Associato", value: "ASSOCIATO" },
];

const ruoloOptions = [
  { label: "Admin", value: "ADMIN" },
  { label: "User", value: "USER" },
];

const ModalFormUtenti: React.FC<ModalFormUtentiProps> = (props) => {
  const {
    arrayUtenti,
    onAddUtente,
    updateUtenti,
    setArrayUtenti,
    action,
    isModalOpen,
    setModalOpen,
    selectedUtente,
    setSelectedUtente,
    onUtenteClick,
  } = props;

  const [utente, setUtente] = useState<Profile>({
    id: "",
    email: "",
    password: null,
    nome: "",
    cognome: "",
    ruolo: "",
    tipoUtente: "",
  });

  const [repeatPassword, setRepeatPassword] = useState<string>("");

  const [errors, setErrors] = useState({
    password: false,
    repeatPassword: false,
  });

  const dispatch: AppDispatch = useDispatch();
  const { token, profilo } = useSelector((state: RootState) => state.login);

  const showAlert = useAlert();

  const isAdmin = profilo.ruolo === "ADMIN";
  const isCurrentUser = utente.id === profilo.id;

  const isModifyingAccount = utente.tipoUtente === "ACCOUNT";

  const canEditFields = (() => {
    if (action === "crea") return true;
    if (isAdmin) {
      if (isModifyingAccount) {
        return false;
      } else {
        return true;
      }
    } else {
      return isCurrentUser;
    }
  })();

  const canEditTipoUtenteAndRuolo = isAdmin;

  const isRuoloDisabled = utente.tipoUtente === "ASSOCIATO" || !canEditTipoUtenteAndRuolo;
  const isTipoUtenteDisabled = !canEditTipoUtenteAndRuolo;

  useEffect(() => {
    if (selectedUtente) {
      setUtente(selectedUtente);
    } else {
      setUtente({
        id: "",
        email: "",
        password: null,
        nome: "",
        cognome: "",
        ruolo: "",
        tipoUtente: "",
      });
    }
    setRepeatPassword("");
    setErrors({ password: false, repeatPassword: false });
  }, [selectedUtente]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "repeatPassword") {
      setRepeatPassword(value);
      setErrors((prevErrors) => ({ ...prevErrors, repeatPassword: false }));
    } else {
      setUtente((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (name === "password") {
        setErrors((prevErrors) => ({ ...prevErrors, password: false }));
      }
    }
  };

  const handleTipoUtenteChange = (e: { value: string }) => {
    const newTipoUtente = e.value;
    setUtente((prev) => ({
      ...prev,
      tipoUtente: newTipoUtente,
      ruolo: newTipoUtente === "ASSOCIATO" ? "USER" : prev.ruolo,
    }));
  };

  const handleRuoloChange = (e: { value: string }) => {
    setUtente((prev) => ({
      ...prev,
      ruolo: e.value,
    }));
  };

  const handleCloseModal = () => {
    setUtente({
      id: "",
      email: "",
      password: null,
      nome: "",
      cognome: "",
      ruolo: "",
      tipoUtente: "",
    });
    setRepeatPassword("");
    setErrors({ password: false, repeatPassword: false });
    setSelectedUtente(null);
    setModalOpen(false);
  };

  const handleSubmit = () => {
    // Validation for required fields
    if (!utente.email || !utente.nome || !utente.cognome || !utente.ruolo || !utente.tipoUtente) {
      showAlert("danger", "Errore", "Tutti i campi sono obbligatori.", "bottom-end", 3000);
      return;
    }

    // If creating an ACCOUNT user, validate passwords
    if (utente.tipoUtente === "ACCOUNT" && action === "crea") {
      if (!utente.password || !repeatPassword) {
        showAlert("danger", "Errore", "Inserisci e ripeti la password.", "bottom-end", 3000);
        setErrors({ password: !utente.password, repeatPassword: !repeatPassword });
        return;
      }

      if (utente.password !== repeatPassword) {
        showAlert("danger", "Errore", "Le password non coincidono.", "bottom-end", 3000);
        setErrors({ password: true, repeatPassword: true });
        return;
      }
    }

    // Proceed with submit
    if (action === "crea") {
      dispatch(allPost(urlutenti, token, utente, showAlert)).then((idUtente) => {
        if (idUtente) {
          showAlert("success", "Creato", "Utente creato con successo.", "bottom-end", 3000);
          const nuovoUtente = { ...utente, id: idUtente };
          onAddUtente(nuovoUtente);
          handleCloseModal();
        } else {
          console.error("Creazione dell'utente fallita");
        }
      });
    } else {
      dispatch(allPut(`${urlutenti}/${utente.id}`, token, utente, showAlert)).then((updatedUtente) => {
        if (updatedUtente) {
          showAlert("success", "Salvato", "Utente modificato con successo.", "bottom-end", 3000);
          updateUtenti(updatedUtente);
          handleCloseModal();
        } else {
          console.error("Modifica dell'utente fallita");
        }
      });
    }
  };

  const errorStyle = {
    border: "1px solid red",
  };

  return (
    <>
      <Dialog
        visible={isModalOpen}
        header={
          <>
            <h6>
              <b>{action === "crea" ? "Crea Nuovo Utente" : "Modifica Utente"}</b>
            </h6>
            <hr />
          </>
        }
        style={{ width: "40vw", borderRadius: "24px" }}
        className="custom-dialog mb-5"
        onHide={handleCloseModal}
      >
        <div className="modal-content mt-3" style={{ borderRadius: "24px" }}>
          <Row>
            <Col xs={6}>
              <Form.Group className="mb-3 d-flex align-items-center">
                <InputText
                  name="nome"
                  value={utente.nome}
                  onChange={handleChange}
                  placeholder="Nome"
                  style={{ width: "100%" }}
                  disabled={!canEditFields}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group className="mb-3 d-flex align-items-center">
                <InputText
                  name="cognome"
                  value={utente.cognome}
                  onChange={handleChange}
                  placeholder="Cognome"
                  style={{ width: "100%" }}
                  disabled={!canEditFields}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-3 d-flex align-items-center">
                <InputText
                  name="email"
                  value={utente.email}
                  onChange={handleChange}
                  placeholder="Email"
                  style={{ width: "100%" }}
                  disabled={!canEditFields}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group className="mb-3 d-flex align-items-center">
                <Dropdown
                  name="tipoUtente"
                  value={utente.tipoUtente}
                  options={tipoUtenteOptions}
                  onChange={handleTipoUtenteChange}
                  placeholder="Tipo Utente"
                  style={{ width: "100%" }}
                  disabled={isTipoUtenteDisabled}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group className="mb-3 d-flex align-items-center">
                <Dropdown
                  name="ruolo"
                  value={utente.ruolo}
                  options={ruoloOptions}
                  onChange={handleRuoloChange}
                  placeholder="Ruolo"
                  style={{ width: "100%" }}
                  disabled={isRuoloDisabled}
                />
              </Form.Group>
            </Col>
            {utente.tipoUtente === "ACCOUNT" && action === "crea" && (
              <>
                <Col xs={12}>
                  <Form.Group className="mb-3 d-flex align-items-center">
                    <Password
                      name="password"
                      value={utente.password || ""}
                      onChange={handleChange}
                      placeholder="Inserisci Password"
                      toggleMask
                      promptLabel="Inserisci una password"
                      weakLabel="Debole"
                      mediumLabel="Media"
                      strongLabel="Forte"
                      feedback={true}
                      inputStyle={{
                        width: "100%",
                        height: "46px",
                        display: "block",
                        borderRadius: "8px",
                        ...(errors.password && errorStyle),
                      }}
                      style={{
                        width: "100%",
                        display: "block",
                        height: "46px",
                        borderRadius: "8px",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group className="mb-3 d-flex align-items-center">
                    <Password
                      name="repeatPassword"
                      value={repeatPassword}
                      onChange={handleChange}
                      placeholder="Ripeti Password"
                      toggleMask
                      promptLabel="Ripeti la password"
                      weakLabel="Debole"
                      mediumLabel="Media"
                      strongLabel="Forte"
                      feedback={false}
                      inputStyle={{
                        width: "100%",
                        height: "46px",
                        display: "block",
                        borderRadius: "8px",
                        ...(errors.repeatPassword && errorStyle),
                      }}
                      style={{
                        width: "100%",
                        display: "block",
                        height: "46px",
                        borderRadius: "8px",
                      }}
                    />
                  </Form.Group>
                </Col>
              </>
            )}
          </Row>
        </div>

        <div className="d-flex justify-content-end align-items-center">
          <Button className="button d-flex px-3" onClick={handleSubmit}>
            <i className="pi pi-save me-2"></i>
            <p className="p-0 m-0">Salva</p>
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default ModalFormUtenti;
